// The band of a rock cell on paper (R3310).
//
// The server states no depth band for a ridge cell, so the band field has a
// hole there. In the dark identity the depth tints lie OVER the terrain and
// the hole is clear rock. On paper the bands are the ground, under the
// terrain (R3309), and the smooth ridge does not fill the hex cells it
// stands on: the paper shows in the corners of the hole as cream hexagons.
// This function gives each rock cell the band of the water that touches it,
// so the ground runs under the ridge and no corner shows. It copies the
// field, because the chart traces the band edges off the field as the server
// states it.
//
// The module opens no GL and no DOM.

import { neighborAt } from '../world';

/**
 * A copy of `bands` in which each cell of `blocked` equal to `rock` and
 * with band 0 takes the band that most of its banded neighbours have. A tie
 * goes to the shallower band. The fill spreads one ring at a time, so a
 * ridge several cells wide gets the band of the nearest water. A rock cell
 * with no banded cell near it stays 0.
 */
export function fillRockBands(
  bands: Uint8Array, blocked: Uint8Array | null, cols: number, rows: number, rock: number,
): Uint8Array {
  const out = bands.slice();
  if (!blocked || blocked.length !== bands.length) return out;
  let open: number[] = [];
  for (let i = 0; i < out.length; i++) if (out[i] === 0 && blocked[i] === rock) open.push(i);
  const votes = [0, 0, 0, 0, 0];
  while (open.length > 0) {
    const take: [number, number][] = [];
    const rest: number[] = [];
    for (const i of open) {
      votes.fill(0);
      for (let k = 0; k < 6; k++) {
        const n = neighborAt(i, k, cols, rows);
        const b = n >= 0 ? out[n] : 0;
        if (b >= 1 && b <= 4) votes[b]++;
      }
      let best = 0;
      for (let b = 1; b <= 4; b++) if (votes[b] > (votes[best] ?? 0)) best = b;
      if (best > 0) take.push([i, best]); else rest.push(i);
    }
    // A ring is written after it is read, so the order of the cells does not matter.
    if (take.length === 0) break;
    for (const [i, b] of take) out[i] = b;
    open = rest;
  }
  return out;
}
